import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/sections/Footer";
import productsContent, { PRODUCTS, PARTNER_PRODUCTS } from "@/i18n/products";
import { useI18n } from "@/i18n/I18nProvider";
import { ArrowUpRight, ArrowRight, ShieldCheck, Sparkles, Check } from "lucide-react";
import Reveal, { MaskedLine, fadeUp, stagger } from "@/components/motion/Reveal";

export default function ProductsPage() {
  const { locale } = useI18n();
  const p = productsContent[locale] || productsContent.en;

  useEffect(() => {
    document.title = "Products — Smart Terminals";
  }, []);

  return (
    <main data-testid="products-page" className="bg-white text-ink-primary font-body">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-white overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />
        <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
        <div className="container-pro relative">
          <div className="overline">{p.sectionKicker}</div>
          <h1 className="mt-3 h1-display text-4xl sm:text-5xl md:text-6xl max-w-3xl">
            <MaskedLine delay={0.1}>{p.sectionTitle1}</MaskedLine>
            <MaskedLine delay={0.22}><span className="text-brand-red">{p.sectionTitle2}</span></MaskedLine>
          </h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-ink-secondary leading-relaxed">{p.sectionBody}</p>
        </div>
      </section>

      {/* Product blocks */}
      {PRODUCTS.map((slug, i) => (
        <ProductBlock key={slug} product={p.catalog[slug]} common={p.common} reverse={i % 2 === 1} />
      ))}

      {/* Partner catalog — powered by FocusR */}
      <PartnerCatalog catalog={p.catalog} partnerBadge={p.partnerBadge} />

      {/* CTA */}
      <section className="section-pad bg-surface-dark text-white">
        <Reveal className="container-pro text-center">
          <div className="overline">// Get started</div>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl font-medium tracking-tight text-white">
            {p.common.cta}
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-white/60">{p.common.ctaBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="/#contact" className="btn-primary" data-testid="products-cta-contact">
              {p.common.ctaContact} <ArrowRight size={16} data-mirror-rtl="true" />
            </a>
          </div>
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}

const ProductBlock = ({ product, common, reverse }) => {
  const isTeal = product.accent === "teal";
  return (
    <section
      id={product.slug}
      data-testid={`product-block-${product.slug}`}
      className={`py-20 md:py-28 ${isTeal ? "bg-surface-dark text-white" : "bg-surface-muted"}`}
    >
      <Reveal variants={fadeUp} className="container-pro grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
  <div className="flex items-center gap-4 mb-6">
            <span className="relative inline-flex h-14 w-14 items-center justify-center">
              <span className={`absolute inset-0 rounded-lg ${isTeal ? "bg-brand-teal" : "bg-brand-black"}`} />
              <span className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-sm bg-brand-red" />
              <span className="relative">
                {isTeal ? <Sparkles size={22} className="text-white" /> : <ShieldCheck size={22} className="text-white" />}
              </span>
            </span>
            <div>
              <div className={`font-heading text-2xl font-semibold ${isTeal ? "text-white" : "text-ink-primary"}`}>
                {product.name}
              </div>
              <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${isTeal ? "text-white/40" : "text-ink-muted"}`}>
                Smart Terminals · {product.version}
              </div>
            </div>
          </div>

          <h2 className={`font-heading text-3xl md:text-4xl font-medium tracking-tight leading-tight ${isTeal ? "text-white" : "text-ink-primary"}`}>
            {product.tagline}
          </h2>
          <p className={`mt-4 text-base leading-relaxed ${isTeal ? "text-white/70" : "text-ink-secondary"}`}>
            {product.short}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {product.badges.map((b) => (
              <span
                key={b}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${
                  isTeal ? "bg-white/5 text-white/80 border-white/15" : "bg-white text-ink-secondary border-line"
                }`}
              >
                {b}
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={product.url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`product-detail-${product.slug}-demo`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded transition-colors ${
                isTeal ? "bg-brand-teal text-white hover:bg-brand-red" : "bg-brand-black text-white hover:bg-brand-red"
              }`}
            >
              {common.tryDemo} <ArrowUpRight size={14} />
            </a>
            <a
              href="/#contact"
              data-testid={`product-detail-${product.slug}-contact`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded border transition-colors ${
                isTeal ? "border-white/20 text-white hover:bg-white/10" : "border-ink-primary text-ink-primary hover:bg-ink-primary hover:text-white"
              }`}
            >
              {common.ctaContact} <ArrowRight size={14} data-mirror-rtl="true" />
            </a>
          </div>
        </div>

        <div className={`lg:col-span-7 space-y-8 ${reverse ? "lg:order-1" : ""}`}>
          {/* Modules */}
          <div>
            <div className={`font-mono text-[10px] uppercase tracking-[0.25em] mb-4 ${isTeal ? "text-brand-red" : "text-brand-red"}`}>
              {common.keyModules}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {product.modules.map((m) => (
                <div
                  key={m.code}
                  className={`p-5 rounded-lg border ${isTeal ? "border-white/10 bg-white/[0.03]" : "border-line bg-white"}`}
                >
                  <div className={`font-mono text-[10px] tracking-widest ${isTeal ? "text-white/40" : "text-ink-muted"}`}>{m.code}</div>
                  <div className={`mt-1.5 font-heading text-base font-semibold ${isTeal ? "text-white" : "text-ink-primary"}`}>{m.title}</div>
                  <p className={`mt-2 text-xs leading-relaxed ${isTeal ? "text-white/60" : "text-ink-secondary"}`}>{m.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div>
            <div className={`font-mono text-[10px] uppercase tracking-[0.25em] mb-4 text-brand-red`}>{common.keyFeatures}</div>
            <ul className="space-y-2.5">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded ${isTeal ? "bg-brand-teal/20 text-brand-teal" : "bg-brand-red/10 text-brand-red"} mt-0.5`}>
                    <Check size={12} />
                  </span>
                  <span className={`text-sm leading-relaxed ${isTeal ? "text-white/80" : "text-ink-secondary"}`}>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Who for */}
          <div>
            <div className={`font-mono text-[10px] uppercase tracking-[0.25em] mb-4 text-brand-red`}>{common.whoFor}</div>
            <div className="flex flex-wrap gap-2">
              {product.whoFor.map((w) => (
                <span
                  key={w}
                  className={`inline-flex items-center px-3 py-1.5 rounded text-xs border ${
                    isTeal ? "bg-white/5 text-white/80 border-white/15" : "bg-white text-ink-primary border-line"
                  }`}
                >
                  {w}
                </span>
              ))}
            </div>
          </div>

          {/* Bonus tool */}
          {product.bonus && (
            <a
              href={product.bonus.url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`product-${product.slug}-bonus`}
              className={`block p-5 rounded-lg border ${
                isTeal ? "border-brand-teal/40 bg-brand-teal/10 hover:bg-brand-teal/20" : "border-brand-red/40 bg-brand-red/5 hover:bg-brand-red/10"
              } transition-colors`}
            >
              <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${isTeal ? "text-brand-teal" : "text-brand-red"}`}>
                {product.bonus.title}
              </div>
              <div className={`mt-1.5 font-heading text-base font-semibold ${isTeal ? "text-white" : "text-ink-primary"}`}>{product.bonus.body}</div>
              <div className={`mt-2 text-xs font-medium ${isTeal ? "text-brand-teal" : "text-brand-red"}`}>{product.bonus.label}</div>
            </a>
          )}
        </div>
      </Reveal>
    </section>
  );
};

const PartnerCatalog = ({ catalog, partnerBadge }) => (
  <section id="partner-catalog" data-testid="partner-catalog" className="section-pad bg-surface-muted relative">
    <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
    <div className="container-pro relative">
      <div className="grid md:grid-cols-12 gap-10 items-end mb-10">
        <div className="md:col-span-7">
          <div className="overline">// Partner catalog</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
            Extended by <span className="text-brand-red">FocusR</span>.
          </h2>
        </div>
        <p className="md:col-span-5 text-ink-secondary">
          Enterprise-grade products from our strategic partner FocusR — deployed and supported locally by Smart Terminals.
        </p>
      </div>

      <Reveal variants={stagger(0.05, 0.08)} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PARTNER_PRODUCTS.map((slug) => {
          const item = catalog[slug];
          if (!item) return null;
          return (
            <a
              key={slug}
              href={item.url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`partner-product-${slug}`}
              className="group relative bg-white border border-line rounded-xl p-6 glow-card overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-red">{partnerBadge}</span>
                <ArrowUpRight size={16} className="text-ink-muted group-hover:text-brand-red transition-colors" />
              </div>
              <h3 className="mt-4 font-heading text-xl font-semibold tracking-tight text-ink-primary">{item.name}</h3>
              <p className="mt-2 text-sm text-ink-secondary leading-relaxed flex-1">{item.tagline}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {item.badges?.map((b) => (
                  <span key={b} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest text-ink-secondary bg-surface-muted border border-line">
                    {b}
                  </span>
                ))}
              </div>
            </a>
          );
        })}
      </Reveal>
    </div>
  </section>
);
